/**
 * Namespaced storage helpers used by the identity module.
 *
 * All keys are prefixed with `solblox.` so we never collide with anything
 * else a host page might stash in localStorage. If `window.localStorage`
 * is missing or throws (private mode, SSR, sandboxed iframes), we silently
 * fall back to an in-memory map; identity then behaves as if the user has
 * a brand-new browser every tab — acceptable for an MVP guest flow.
 *
 * Module load has no side effects beyond constructing the in-memory map.
 */

const PREFIX = "solblox.";

const memory: Map<string, string> = new Map();

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function backend(): StorageLike {
  // Probe lazily on every call so we don't crash at module load and so a
  // delayed-available localStorage (some embedded webviews) is picked up.
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      const probeKey = `${PREFIX}__probe`;
      window.localStorage.setItem(probeKey, "1");
      window.localStorage.removeItem(probeKey);
      return window.localStorage;
    }
  } catch {
    // fall through to memory
  }
  return {
    getItem(key: string): string | null {
      return memory.has(key) ? (memory.get(key) as string) : null;
    },
    setItem(key: string, value: string): void {
      memory.set(key, value);
    },
    removeItem(key: string): void {
      memory.delete(key);
    },
  };
}

function nsKey(key: string): string {
  return `${PREFIX}${key}`;
}

export function readString(key: string): string | null {
  try {
    return backend().getItem(nsKey(key));
  } catch {
    return null;
  }
}

export function writeString(key: string, value: string): void {
  try {
    backend().setItem(nsKey(key), value);
  } catch {
    // best-effort; quota / private mode
  }
}

export function removeKey(key: string): void {
  try {
    backend().removeItem(nsKey(key));
  } catch {
    // best-effort
  }
}

export function readJSON<T>(key: string): T | null {
  const raw = readString(key);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function writeJSON<T>(key: string, value: T): void {
  try {
    writeString(key, JSON.stringify(value));
  } catch {
    // value not serializable; ignore
  }
}
